import { CSSProperties } from "react";
import { useTheme, getColors, getDomainColors, type ThemeColors } from "./theme";

// ─── Theme-aware hooks ────────────────────────────────────────────────

export function useColors(): ThemeColors {
  const { theme } = useTheme();
  return getColors(theme);
}

export function useDomainColors(): Record<string, string> {
  const { theme } = useTheme();
  return getDomainColors(theme);
}

export function useStyles() {
  const colors = useColors();
  return createStyles(colors);
}

// ─── Style generator ──────────────────────────────────────────────────

export function createStyles(colors: ThemeColors) {
  return {
    table: {
      width: "max-content",
      borderCollapse: "collapse",
      background: colors.bg,
    } as CSSProperties,

    th: {
      padding: "4px 6px",
      textAlign: "left",
      fontWeight: 600,
      fontSize: "0.75rem",
      letterSpacing: "0.02em",
      textTransform: "uppercase",
      borderBottom: `1px solid ${colors.borderDark}`,
      background: colors.bgAlt,
      whiteSpace: "nowrap",
      color: colors.textMuted,
      position: "sticky",
      top: 0,
      zIndex: 1,
    } as CSSProperties,

    td: {
      padding: "6px 8px",
      borderBottom: `1px solid ${colors.border}`,
      color: colors.text,
      whiteSpace: "nowrap",
      fontSize: "0.875rem",
    } as CSSProperties,

    input: {
      padding: "6px 8px",
      border: `1px solid ${colors.border}`,
      borderRadius: 0,
      fontSize: "1rem",
      width: "100%",
      boxSizing: "border-box",
      minHeight: "36px",
      background: colors.bg,
      color: colors.text,
    } as CSSProperties,

    select: {
      padding: "6px 8px",
      border: `1px solid ${colors.border}`,
      borderRadius: 0,
      fontSize: "1rem",
      width: "100%",
      boxSizing: "border-box",
      minHeight: "36px",
      background: colors.bg,
      color: colors.text,
      appearance: "auto" as const,
    } as CSSProperties,

    btn: {
      padding: "6px 12px",
      border: `1px solid ${colors.border}`,
      background: colors.bg,
      color: colors.text,
      cursor: "pointer",
      fontSize: "1rem",
      borderRadius: 0,
      minHeight: "36px",
    } as CSSProperties,

    btnPrimary: {
      padding: "6px 12px",
      border: `1px solid ${colors.primary}`,
      background: colors.primary,
      color: "#fff",
      cursor: "pointer",
      fontSize: "1rem",
      borderRadius: 0,
      minHeight: "36px",
    } as CSSProperties,

    btnDanger: {
      padding: "6px 12px",
      border: `1px solid ${colors.danger}`,
      background: colors.bg,
      color: colors.danger,
      cursor: "pointer",
      fontSize: "1rem",
      borderRadius: 0,
      minHeight: "36px",
    } as CSSProperties,

    nav: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "4px 10px",
      padding: "4px 8px",
      minHeight: "40px",
      borderBottom: `1px solid ${colors.border}`,
      background: colors.bg,
      flexShrink: 0,
    } as CSSProperties,

    navLink: {
      textDecoration: "none",
      color: colors.textMuted,
      fontWeight: 500,
      fontSize: "0.875rem",
    } as CSSProperties,

    navLinkActive: {
      textDecoration: "none",
      color: colors.primary,
      fontWeight: 500,
      fontSize: "0.875rem",
    } as CSSProperties,

    page: {
      height: "100dvh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      background: colors.bg,
      fontFamily: "system-ui, -apple-system, sans-serif",
      color: colors.text,
    } as CSSProperties,

    content: {
      flex: 1,
      overflow: "auto",
      display: "flex",
      flexDirection: "column",
    } as CSSProperties,

    grid2: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "4px 12px",
      padding: "8px 12px",
    } as CSSProperties,

    grid4: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr 1fr",
      gap: "4px 12px",
      padding: "8px 12px",
    } as CSSProperties,

    label: {
      fontSize: "0.75rem",
      color: colors.textMuted,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      padding: "4px 0",
    } as CSSProperties,

    section: {
      borderBottom: `1px solid ${colors.border}`,
    } as CSSProperties,

    sectionHeader: {
      padding: "8px 12px",
      fontWeight: 600,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      background: colors.bgAlt,
      borderBottom: `1px solid ${colors.border}`,
      color: colors.text,
    } as CSSProperties,

    toolbar: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 12px",
      borderBottom: `1px solid ${colors.border}`,
    } as CSSProperties,

    // ─── Compact variants (dense case form) ──────────────────────────

    grid3: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr",
      gap: "4px 12px",
      padding: "8px 12px",
    } as CSSProperties,

    inputCompact: {
      padding: "4px 6px",
      border: `1px solid ${colors.border}`,
      borderRadius: 0,
      fontSize: "0.875rem",
      width: "100%",
      boxSizing: "border-box",
      minHeight: "28px",
      background: colors.bg,
      color: colors.text,
    } as CSSProperties,

    selectCompact: {
      padding: "4px 6px",
      border: `1px solid ${colors.border}`,
      borderRadius: 0,
      fontSize: "0.875rem",
      width: "100%",
      boxSizing: "border-box",
      minHeight: "28px",
      background: colors.bg,
      color: colors.text,
      appearance: "auto" as const,
    } as CSSProperties,

    textareaCompact: {
      padding: "4px 6px",
      border: `1px solid ${colors.border}`,
      borderRadius: 0,
      fontSize: "0.875rem",
      width: "100%",
      boxSizing: "border-box",
      minHeight: "40px",
      resize: "vertical",
      background: colors.bg,
      color: colors.text,
    } as CSSProperties,

    labelCompact: {
      fontSize: "0.75rem",
      color: colors.textMuted,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      padding: "4px 0",
      marginBottom: "2px",
    } as CSSProperties,

    thCompact: {
      padding: "4px 6px",
      textAlign: "left",
      fontWeight: 600,
      fontSize: "0.7rem",
      borderBottom: `1px solid ${colors.borderDark}`,
      background: colors.bgAlt,
      whiteSpace: "nowrap",
      color: colors.text,
      position: "sticky",
      top: 0,
      zIndex: 1,
    } as CSSProperties,

    btnSmall: {
      padding: "2px 8px",
      border: `1px solid ${colors.border}`,
      background: colors.bg,
      color: colors.text,
      cursor: "pointer",
      fontSize: "0.75rem",
      borderRadius: 0,
    } as CSSProperties,

    btnDelete: {
      padding: "1px 4px",
      border: `1px solid ${colors.border}`,
      background: colors.bg,
      color: colors.danger,
      cursor: "pointer",
      fontSize: "0.7rem",
      borderRadius: 0,
    } as CSSProperties,
  };
}

// ─── Utility functions ────────────────────────────────────────────────

export function getTriageColor(colors: ThemeColors): Record<string, string> {
  return {
    I: colors.triageI,
    II: colors.triageII,
    III: colors.triageIII,
    IV: colors.triageIV,
  };
}

export const badge = (bg: string): CSSProperties => ({
  display: "inline-block",
  padding: "2px 8px",
  background: bg,
  color: "#fff",
  fontSize: "0.875rem",
  fontWeight: 600,
});
